// The sky over Joutseno: a shader dome whose colours follow the real sun (from daylight.ts), with
// the sun's glow and disc, drifting lit clouds, stars and the moon in its real phase at night.
// The same shader, rendered into a PMREM cube, lights the whole scene (image-based lighting),
// so reflections, fill light and the visible sky always agree. Rebake when the light changes.
import {
  BackSide, Mesh, PMREMGenerator, Scene, ShaderMaterial, SphereGeometry, Uniform, Vector3,
  type Texture, type WebGLRenderer,
} from 'three';
import type { Rgb, SkyPalette } from './daylight';
import { GLSL_NOISE } from './util';

export const SKY_RADIUS = 2600;

const PALETTE_KEYS = [
  'zenithAway', 'zenithSun', 'upperAway', 'upperSun', 'horizonAway', 'horizonSun',
  'glow', 'halo', 'cloudAway', 'cloudSun', 'cloudShadow',
] as const satisfies readonly (keyof SkyPalette)[];
const uniformName = (k: string) => `u${k[0]!.toUpperCase()}${k.slice(1)}`;

const vertex = /* glsl */ `
varying vec3 vDir;
void main() {
  vDir = normalize((modelMatrix * vec4(position, 0.0)).xyz);
  vec4 p = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  gl_Position = p.xyww; // always on the far plane
}`;

const fragment = /* glsl */ `
uniform vec3 uSunDir;
uniform float uTime;
uniform float uSunDisc;   // 0 in the environment capture so the IBL is not dominated by the disc
uniform float uTripIntensity; // for the catnip trip job: 0 = normal sky
uniform float uStars;     // 0 by day, 1 at night
uniform vec3 uMoonDir;
uniform float uMoon;      // moon brightness (0 below the horizon)
${PALETTE_KEYS.map((k) => `uniform vec3 ${uniformName(k)};`).join('\n')}
varying vec3 vDir;
${GLSL_NOISE}

float starHash(vec3 p) { p = fract(p * 0.3183099 + 0.1); p *= 17.0; return fract(p.x * p.y * p.z * (p.x + p.y + p.z)); }

vec3 skyColour(vec3 d) {
  float h = d.y;
  float s = max(dot(d, uSunDir), 0.0);
  // Azimuthal closeness to the sun: the horizon is warmest under the sun, cooler behind.
  vec2 dh = normalize(d.xz + 1e-5), sh = normalize(uSunDir.xz + 1e-5);
  float az = dot(dh, sh) * 0.5 + 0.5;

  vec3 zenith = mix(uZenithAway, uZenithSun, az);
  vec3 upper = mix(uUpperAway, uUpperSun, az);
  vec3 horizon = mix(uHorizonAway, uHorizonSun, pow(az, 2.0));
  float hp = max(h, 0.0);
  vec3 c = mix(horizon, upper, smoothstep(0.0, 0.22, hp));
  c = mix(c, zenith, smoothstep(0.12, 0.6, hp));
  // Below the horizon: a hazy continuation (normally hidden by land and water).
  c = mix(c, horizon * 0.55, smoothstep(0.0, -0.15, h));

  // Sun glow and disc (the disc only while the sun is up).
  float sunUp = smoothstep(-0.02, 0.01, uSunDir.y);
  c += uGlow * pow(s, 6.0);
  c += uHalo * pow(s, 48.0);
  c += vec3(30.0, 20.0, 9.0) * mix(vec3(1.0, 0.75, 0.6), vec3(1.25, 1.45, 1.75), smoothstep(0.05, 0.4, uSunDir.y))
    * smoothstep(0.99955, 0.99975, s) * uSunDisc * sunUp;

  if (h > 0.0) {
    // Stars: one possible star per cell of a fine grid on the sphere, twinkling a little.
    if (uStars > 0.0) {
      vec3 g = d * 260.0;
      vec3 cell = floor(g);
      float rnd = starHash(cell);
      if (rnd > 0.93) {
        vec3 at = cell + 0.5 + (vec3(starHash(cell + 7.1), starHash(cell + 3.3), starHash(cell + 5.7)) - 0.5) * 0.6;
        float dist = length(g - at);
        float tw = 0.75 + 0.25 * sin(uTime * (1.5 + rnd * 3.0) + rnd * 60.0);
        float mag = pow((rnd - 0.93) / 0.07, 3.0);
        c += vec3(0.9, 0.95, 1.1) * smoothstep(0.32, 0.0, dist) * (0.25 + mag * 2.6) * tw * uStars * smoothstep(0.0, 0.12, h);
      }
    }
    // Moon: a lit sphere, its phase set by where the sun is.
    if (uMoon > 0.0) {
      float md = dot(d, uMoonDir);
      const float R = 0.02;
      vec3 right = normalize(cross(uMoonDir, vec3(0.0, 1.0, 0.0)));
      vec3 up = cross(right, uMoonDir);
      vec2 p = vec2(dot(d, right), dot(d, up)) / R;
      float r2 = dot(p, p);
      if (md > 0.0 && r2 < 1.0) {
        vec3 n = right * p.x + up * p.y - uMoonDir * sqrt(1.0 - r2);
        float lit = smoothstep(-0.06, 0.08, dot(n, uSunDir));
        float maria = 0.82 + 0.18 * envFbm(p * 2.5 + 4.0);
        float edge = smoothstep(1.0, 0.9, r2);
        c = mix(c, vec3(1.6, 1.55, 1.42) * maria * lit + c * 0.4, edge * uMoon);
      }
      c += vec3(0.25, 0.27, 0.32) * pow(max(md, 0.0), 900.0) * uMoon * 0.5;
    }
  }

  // Clouds: two fbm layers projected onto a high plane, lit on the sun side, cooler away from
  // it, with dark undersides.
  if (h > 0.0) {
    vec2 uv = d.xz / (h + 0.06);
    float t = uTime * 0.004;
    float n = envFbm(uv * 0.55 + vec2(t, t * 0.4));
    float n2 = envFbm(uv * 1.6 - vec2(t * 1.7, 0.0) + n);
    float band = smoothstep(0.02, 0.12, h) * (1.0 - smoothstep(0.35, 0.7, h));
    float cover = smoothstep(0.52, 0.78, n * 0.75 + n2 * 0.35) * band;
    float edge = pow(s, 4.0);
    vec3 lit = mix(uCloudAway, uCloudSun, edge);
    vec3 cloud = mix(uCloudShadow, lit, smoothstep(0.35, 0.95, n2));
    c = mix(c, cloud, cover * 0.85);
  }

  // Catnip trip hook: the trip job can blend in its own palette through uTripIntensity.
  if (uTripIntensity > 0.0) {
    float k = sin(atan(d.z, d.x) * 6.0 + uTime * 1.5 + h * 12.0) * 0.5 + 0.5;
    vec3 trip = mix(vec3(1.2, 0.15, 0.3), vec3(0.1, 1.1, 1.2), k);
    trip = mix(trip, vec3(0.5, 1.3, 0.2), smoothstep(0.6, 1.0, sin(h * 20.0 - uTime * 2.0) * 0.5 + 0.5));
    c = mix(c, trip, uTripIntensity);
  }
  return c;
}

void main() {
  gl_FragColor = vec4(skyColour(normalize(vDir)), 1.0);
}`;

type SkyUniforms = Record<string, Uniform<number> | Uniform<Vector3>>;

function makeUniforms(): SkyUniforms {
  const u: SkyUniforms = {
    uSunDir: new Uniform(new Vector3(0, 0.2, 1)),
    uTime: new Uniform(0),
    uSunDisc: new Uniform(1),
    uTripIntensity: new Uniform(0),
    uStars: new Uniform(0),
    uMoonDir: new Uniform(new Vector3(0, 1, 0)),
    uMoon: new Uniform(0),
  };
  for (const k of PALETTE_KEYS) u[uniformName(k)] = new Uniform(new Vector3());
  return u;
}

export class Sky {
  readonly mesh: Mesh<SphereGeometry, ShaderMaterial>;
  readonly uniforms = makeUniforms() as SkyUniforms & {
    uSunDir: Uniform<Vector3>; uTime: Uniform<number>; uSunDisc: Uniform<number>; uTripIntensity: Uniform<number>;
    uStars: Uniform<number>; uMoonDir: Uniform<Vector3>; uMoon: Uniform<number>;
  };
  private envTarget: { texture: Texture; dispose(): void } | null = null;

  constructor() {
    const mat = new ShaderMaterial({
      uniforms: this.uniforms, vertexShader: vertex, fragmentShader: fragment,
      side: BackSide, depthWrite: false, fog: false,
    });
    this.mesh = new Mesh(new SphereGeometry(SKY_RADIUS, 48, 24), mat);
    this.mesh.name = 'sky';
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = -10;
  }

  /** Sets the sky's colours, the true sun and moon directions, the stars and the moon's brightness. */
  set(palette: SkyPalette, sunDir: Vector3, moonDir: Vector3, stars: number, moon: number): void {
    for (const k of PALETTE_KEYS) (this.uniforms[uniformName(k)]!.value as Vector3).set(...(palette[k] as Rgb));
    this.uniforms.uSunDir.value.copy(sunDir).normalize();
    this.uniforms.uMoonDir.value.copy(moonDir).normalize();
    this.uniforms.uStars.value = stars;
    this.uniforms.uMoon.value = moon;
  }

  /**
   * Renders the sky (without the sun disc, stars or moon) into a prefiltered cube for
   * image-based lighting. `ground` is the light bouncing up from the ground below the horizon.
   */
  bakeEnvironment(renderer: WebGLRenderer, ground: Rgb): Texture {
    const pmrem = new PMREMGenerator(renderer);
    const scene = new Scene();
    const probe = new Mesh(new SphereGeometry(100, 48, 24), this.mesh.material.clone());
    const u = makeUniforms();
    for (const [k, v] of Object.entries(this.uniforms)) {
      const value = v.value;
      u[k]!.value = typeof value === 'number' ? value : value.clone();
    }
    u.uTime!.value = 0; u.uSunDisc!.value = 0; u.uTripIntensity!.value = 0; u.uStars!.value = 0; u.uMoon!.value = 0;
    probe.material.uniforms = u;
    const groundMesh = new Mesh(new SphereGeometry(90, 32, 16, 0, Math.PI * 2, Math.PI / 2 + 0.02, Math.PI / 2), new ShaderMaterial({
      side: BackSide, depthWrite: false,
      uniforms: { uGround: new Uniform(new Vector3(...ground)) },
      vertexShader: 'void main(){ gl_Position = projectionMatrix * modelViewMatrix * vec4(position,1.0); }',
      fragmentShader: 'uniform vec3 uGround; void main(){ gl_FragColor = vec4(uGround, 1.0); }',
    }));
    scene.add(probe, groundMesh);
    const target = pmrem.fromScene(scene, 0.02, 0.1, 500);
    this.envTarget?.dispose();
    this.envTarget = target;
    pmrem.dispose();
    probe.geometry.dispose(); probe.material.dispose();
    groundMesh.geometry.dispose(); (groundMesh.material as ShaderMaterial).dispose();
    return target.texture;
  }

  update(timeS: number, camPos: Vector3): void {
    this.uniforms.uTime.value = timeS;
    this.mesh.position.copy(camPos);
  }
}
